import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Chat/Answer";
import { motion } from "motion/react";
import { CSSProperties, useMemo } from "react";
import { cn } from "utils";
import { Markdown } from "../Markdown";
import { SourceType } from "./Source";
import { type ChatJsonRenderContextValue } from "./jsonRender/ChatJsonRenderContext";
import { type DynamicUiItem } from "./jsonRender/parseDynamicUiBlocks";

const I18n = I18nUtil.createScoped(Translations);

const EDGENCHART_OPEN_RE = /<edgenchart\b[^>]*>[\s\S]*?<\/edgenchart>/g;
const EDGENCHART_PARTIAL_RE = /<edgenchart\b(?:(?!<\/edgenchart>)[\s\S])*$/;

function processEdgenChartStream(content: string, isStreaming: boolean) {
  if (!isStreaming || !content.includes("<edgenchart")) {
    return { processed: content, hasPendingChart: false };
  }
  const withoutComplete = content.replace(EDGENCHART_OPEN_RE, "\x00");
  const hasPartial = EDGENCHART_PARTIAL_RE.test(withoutComplete);
  if (!hasPartial) {
    return { processed: content, hasPendingChart: false };
  }
  const processed = content.replace(EDGENCHART_PARTIAL_RE, "");
  return { processed, hasPendingChart: true };
}

interface AnswerProps {
  markdown: string;
  dynamicUiItems?: DynamicUiItem[];
  dynamicUiRenderContext?: ChatJsonRenderContextValue;
  entering?: boolean;
  source?: SourceType[];
  showSelect?: boolean;
  containerStyle?: CSSProperties;
  enableNavigation?: boolean;
  currentIndex?: number;
  className?: string;
}

const Answer = ({
  markdown,
  entering,
  dynamicUiItems,
  dynamicUiRenderContext,
  source,
  showSelect,
  containerStyle,
  enableNavigation = false,
  className,
  currentIndex = 0,
}: AnswerProps) => {
  const { processed, hasPendingChart } = useMemo(
    () => processEdgenChartStream(markdown, !!entering),
    [markdown, entering],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 1.5 } }}
      className={cn("w-full flex-1 overflow-auto", className)}
    >
      <div style={containerStyle}>
        <Markdown
          source={source}
          showSelect={showSelect}
          enableNavigation={enableNavigation}
          currentIndex={currentIndex}
          dynamicUiItems={dynamicUiItems}
          dynamicUiRenderContext={dynamicUiRenderContext}
          className={cn({
            "[&_td]:!min-w-[50px] [&_th]:!min-w-[50px]": showSelect,
          })}
        >
          {processed}
        </Markdown>
        {hasPendingChart && (
          <div className="my-4 flex items-center gap-2 rounded-xl bg-background_white_overlay5 px-4 py-3 animate-pulse">
            <div className="h-4 w-4 rounded-full border-2 border-content_brand_primary border-t-transparent animate-spin" />
            <span className="body_s text-content_tertiary">
              {I18n.t("Generating chart...")}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Answer;
